import { useLingui } from '@lingui/react/macro';

import { Button, Text } from '@rover/kibble/core';
import useTheme from '@rover/kibble/themes/useTheme';

/** Line height in pixels for overflow calculations */
export const LINE_HEIGHT = 24;

/** Number of lines for small size */
export const NUMBER_OF_LINES_SMALL = 2;
/** Number of lines for medium size */
export const NUMBER_OF_LINES_MEDIUM = 4;
/** Number of lines for large size */
export const NUMBER_OF_LINES_LARGE = 8;

/** Size presets determining number of visible lines */
export type OverflowTextSizes = 'small' | 'medium' | 'large';
/** Layout variants for expand/collapse button */
export type VariantSizes = 'inline' | 'block';

/** Props for OverflowText component */
export type Props = {
  /** Custom number of lines (overrides size preset) */
  lines?: number;
  /** Size preset determining number of visible lines */
  size?: OverflowTextSizes;
  /** Layout variant for expand/collapse button */
  variant?: VariantSizes;
  /** Custom "read more" button text */
  readMoreText?: string;
  /** Custom "read less" button text */
  readLessText?: string;
  /** Text content to display */
  children?: React.ReactNode;
};

/** Props for expand/collapse button */
export type ExpandCollapseProps = {
  /** Current expanded state */
  isExpanded: boolean;
  /** Toggle expand/collapse */
  expandCollapseToggle: () => void;
  /** ID for paragraph element for ARIA */
  paragraphId: string;
  /** "Read more" button text */
  readMoreText: string;
  /** "Read less" button text */
  readLessText: string;
  /** Layout variant */
  variant: VariantSizes;
};

export const ExpandCollapseButton = ({
  isExpanded,
  expandCollapseToggle,
  paragraphId,
  readMoreText,
  readLessText,
  variant,
}: ExpandCollapseProps): JSX.Element => {
  const { t } = useLingui();
  const isNative = process.env.JS_ENV_NATIVE;
  const theme = useTheme();
  const { colors } = theme;

  let variantStyles = {};
  // the padding varies by variant and platform
  if (variant === 'inline' && isNative) {
    variantStyles = { paddingY: '0', paddingRight: '0', paddingLeft: '10x' };
  }
  if (variant === 'inline' && !isNative) {
    variantStyles = { paddingY: '0', paddingRight: '0', paddingLeft: '2x' };
  }
  if (variant === 'block') {
    variantStyles = { padding: '0' };
  }

  return (
    <Button
      data-testid="overflow-text-button"
      variant="flat"
      onClick={expandCollapseToggle}
      aria-expanded={isExpanded}
      aria-controls={paragraphId}
      aria-label={t`Reveal visually hidden text`}
      {...variantStyles}
    >
      <Text
        rxnProps={{
          style: {
            textDecorationLine: 'underline',
            color: colors.interactive_link.primary_base.toString(),
          },
        }}
      >
        {isExpanded ? readLessText : readMoreText}
      </Text>
    </Button>
  );
};

export const getTargetNumLines = (size: OverflowTextSizes, lines = 0): number => {
  if (lines > 0) {
    return lines;
  }
  switch (size) {
    case 'small':
      return NUMBER_OF_LINES_SMALL;
    case 'medium':
      return NUMBER_OF_LINES_MEDIUM;
    case 'large':
      return NUMBER_OF_LINES_LARGE;
    default:
      return NUMBER_OF_LINES_LARGE;
  }
};
